"use client";

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { Plus, CreditCard, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { usePaymentAccountMutations } from "@/hooks/usePaymentAccountMutations";
import { PaymentAccountType, PaymentAccountStatus, formatPaymentAccountType } from "@/types/payment-account";
import { bankLogoService } from "@/services/bankLogoService";

// Schema simplificado para cadastro rápido de conta de pagamento
const quickAddPaymentAccountSchema = z.object({
  name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres").max(50, "Nome deve ter no máximo 50 caracteres"),
  type: z.nativeEnum(PaymentAccountType, { required_error: "Selecione um tipo de conta" }),
  bank_name: z.string().optional(),
  initial_balance: z.union([z.string(), z.number()]).transform((val) => {
    if (typeof val === 'string') {
      const num = parseFloat(val.replace(/[^0-9,-]/g, '').replace(',', '.'));
      return isNaN(num) ? 0 : num;
    }
    return isNaN(val) ? 0 : val;
  }).default(0),
  description: z.string().optional(),
  is_default: z.boolean().default(false),
});

type QuickAddPaymentAccountSchema = z.infer<typeof quickAddPaymentAccountSchema>;

interface QuickAddPaymentAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPaymentAccountAdded: (paymentAccountId: string) => void;
}

// Lista de bancos populares para autocomplete
const popularBanks = [
  "Banco do Brasil",
  "Caixa Econômica Federal",
  "Itaú Unibanco",
  "Bradesco",
  "Santander",
  "Banco Inter",
  "NuBank",
  "BTG Pactual",
  "Sicredi",
  "Banco Safra",
];

export function QuickAddPaymentAccountModal({ isOpen, onClose, onPaymentAccountAdded }: QuickAddPaymentAccountModalProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [isFetchingLogo, setIsFetchingLogo] = useState(false);
  const { createAccount } = usePaymentAccountMutations();

  const form = useForm<QuickAddPaymentAccountSchema>({
    resolver: zodResolver(quickAddPaymentAccountSchema),
    defaultValues: {
      name: "",
      type: PaymentAccountType.CHECKING,
      bank_name: "",
      initial_balance: 0,
      description: "",
      is_default: false,
    },
  });

  const handleBankNameChange = (value: string) => {
    form.setValue("bank_name", value);

    // Fetch logo when bank name changes
    if (value.trim()) {
      setIsFetchingLogo(true);
      bankLogoService.getLogoUrlForBank(value)
        .finally(() => {
          setIsFetchingLogo(false);
        });
    }
  };

  const handleSubmit = async (data: QuickAddPaymentAccountSchema) => {
    setIsSaving(true);

    try {
      // Preparar dados para criação
      const accountData = {
        name: data.name,
        description: data.description || "",
        type: data.type,
        bank_name: data.bank_name || "",
        account_number: "",
        agency: "",
        initial_balance: Number(data.initial_balance),
        status: PaymentAccountStatus.ACTIVE,
        color: "#3b82f6",
        is_default: data.is_default,
      };

      const newAccount = await createAccount(accountData);

      if (newAccount) {
        // Passar o ID da conta criada para o formulário principal
        onPaymentAccountAdded(newAccount.id);

        // Resetar formulário e fechar modal
        form.reset();
        onClose();

        toast.success("Conta de pagamento adicionada com sucesso!");
      } else {
        throw new Error("Não foi possível criar a conta de pagamento");
      }
    } catch (error: any) {
      console.error("Erro ao criar conta de pagamento:", error);
      toast.error(error.message || "Erro ao criar conta de pagamento");
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-focus no nome quando o modal abre
  React.useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const nameInput = document.getElementById('payment-account-name');
        if (nameInput) {
          nameInput.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Adicionar Nova Conta de Pagamento
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome da Conta *</FormLabel>
                  <FormControl>
                    <Input
                      id="payment-account-name"
                      placeholder="Ex: Conta Corrente Itaú"
                      {...field}
                      disabled={isSaving}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Conta *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} disabled={isSaving}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(PaymentAccountType).map((type) => (
                        <SelectItem key={type} value={type}>
                          {formatPaymentAccountType(type)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="bank_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Banco</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder="Digite o nome do banco"
                        list="banks"
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => handleBankNameChange(e.target.value)}
                        disabled={isSaving}
                      />
                      {isFetchingLogo && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <datalist id="banks">
                    {popularBanks.map((bank) => (
                      <option key={bank} value={bank} />
                    ))}
                  </datalist>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="initial_balance"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Saldo Inicial</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="0,00"
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*[.,]?\d*$/.test(value)) {
                          field.onChange(value);
                        }
                      }}
                      disabled={isSaving}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Descreva o propósito desta conta (opcional)"
                      rows={2}
                      {...field}
                      disabled={isSaving}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_default"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isSaving}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Definir como conta padrão</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Conta
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}